/**
 * Lazy loading implementation for VexFlow components
 * 
 * This file provides a mechanism to dynamically load VexFlow components
 * only when they are needed, reducing the initial bundle size.
 */

import React, { useState, useEffect } from 'react';

// Store for loaded modules
let vexflowModules = null;

/**
 * Asynchronously loads the VexFlow modules
 * @returns {Promise<Object>} A promise that resolves to an object containing all VexFlow exports
 */
export const loadVexFlowModules = async () => {
  if (vexflowModules) {
    return vexflowModules;
  }

  try {
    const vexflow = await import('vexflow');
    vexflowModules = vexflow;
    return vexflowModules;
  } catch (error) {
    throw error;
  }
};

/**
 * A React component that loads VexFlow modules asynchronously and renders its children
 * only when VexFlow is loaded.
 * 
 * Usage:
 * <VexFlowLoader>
 *   {(vexflow) => (
 *     <YourComponent vexflow={vexflow} />
 *   )}
 * </VexFlowLoader>
 */
const VexFlowLoader = ({ children, fallback = <div>Loading music notation...</div> }) => {
  const [vexflow, setVexflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadModules = async () => {
      try {
        const modules = await loadVexFlowModules();
        if (isMounted) {
          setVexflow(modules);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err);
          setLoading(false);
        }
      }
    };
    
    loadModules();
    
    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return fallback;
  }

  if (error) {
    return <div>Error loading music notation library. Please try again later.</div>;
  }

  return children(vexflow);
};

export default VexFlowLoader; 